import React, { useState } from 'react';
import { Meeting, OutcomesConflictError, UpdateSummaryRequest } from '../../../../types/meeting';
import MeetingOutcomesEditor from './MeetingOutcomesEditor';

interface MeetingMinutesPanelProps {
  meeting: Meeting;
  isGenerating: boolean;
  canEdit: boolean;
  approved: boolean;
  onGenerate: () => void;
  onSave: (draft: UpdateSummaryRequest) => Promise<void>;
  // Refetches the meeting so the workspace holds the other editor's version.
  onReload?: () => Promise<Meeting>;
}

const initials = (name: string): string =>
  name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const outcomeClass = (outcome: string): string => {
  const o = outcome.toLowerCase();
  if (o.includes('carr') || o.includes('approv') || o.includes('pass')) return 'carried';
  if (o.includes('reject') || o.includes('fail') || o.includes('declin')) return 'rejected';
  return 'deferred';
};

/** Splits a key point into a bold lead-in and the remainder when the text
 *  starts with a "Topic: detail" or "Topic — detail" pattern. */
const splitKeyPoint = (point: string): { lead: string | null; rest: string } => {
  const match = point.match(/^([^:—–]{3,60})(:|—|–)\s+(.+)$/);
  if (match) return { lead: match[1].trim(), rest: match[3] };
  return { lead: null, rest: point };
};

const MeetingMinutesPanel: React.FC<MeetingMinutesPanelProps> = ({
  meeting,
  isGenerating,
  canEdit,
  approved,
  onGenerate,
  onSave,
  onReload,
}) => {
  const summary = meeting.summary;
  const hasTranscript = Boolean(meeting.transcript_text);
  const showGenerateButton = !(meeting.status === 'completed' && summary);
  const [showKeyPoints, setShowKeyPoints] = useState(true);
  const [showDecisions, setShowDecisions] = useState(true);
  const [showActions, setShowActions] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [reloaded, setReloaded] = useState(false);
  // The version the editor was seeded from. Pinned on entry so a reload that
  // pulls in someone else's save does not remount the editor and wipe the draft.
  const [editingSummary, setEditingSummary] = useState<Meeting['summary']>(null);

  const beginEditing = () => {
    setEditingSummary(summary);
    setEditing(true);
    setSaveError(null);
    setConflict(null);
    setReloaded(false);
  };

  const stopEditing = () => {
    setEditing(false);
    setEditingSummary(null);
    setSaveError(null);
    setConflict(null);
    setReloaded(false);
  };

  const handleSave = async (draft: UpdateSummaryRequest) => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
      stopEditing();
    } catch (err) {
      // A conflict is not a failed save to retry blindly: someone else's text is
      // now on the server. Stay in the editor with the draft intact and let the
      // user decide, rather than discarding either version for them.
      if (err instanceof OutcomesConflictError) {
        setConflict(err.message);
        setReloaded(false);
      } else {
        setConflict(null);
        setSaveError(err instanceof Error ? err.message : 'Failed to save outcomes');
      }
    } finally {
      setSaving(false);
    }
  };

  // Pulls the other editor's version into the panel behind the editor and
  // refreshes the concurrency token, so a deliberate re-save now succeeds. The
  // draft is untouched — discarding it stays an explicit Cancel.
  const handleReload = async () => {
    if (!onReload) return;
    setReloading(true);
    setSaveError(null);
    try {
      await onReload();
      setReloaded(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reload outcomes');
    } finally {
      setReloading(false);
    }
  };

  if (editing && editingSummary) {
    return (
      <MeetingOutcomesEditor
        summary={editingSummary}
        attendees={meeting.attendees || []}
        approved={approved}
        saving={saving}
        error={saveError}
        conflict={conflict}
        reloading={reloading}
        reloaded={reloaded}
        latestSummary={summary}
        onReload={onReload ? handleReload : undefined}
        onSave={handleSave}
        onCancel={stopEditing}
      />
    );
  }

  const decisions = summary?.decisions || [];
  const actionItems = summary?.action_items || [];
  const keyPoints = summary?.key_points || [];
  const carriedCount = decisions.filter((d) => outcomeClass(d.outcome) === 'carried').length;
  const unassignedActions = actionItems.filter((a) => !a.owner).length;

  return (
    <>
      <div className="meeting-panel">
        <div className="meeting-panel-header">
          <div>
            <h3 className="meeting-panel-title">Meeting Minutes &amp; Outcomes</h3>
            {summary?.generated_at && (
              <p className="meeting-panel-subtitle">
                Generated {new Date(summary.generated_at).toLocaleString()}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && summary && (
              <button
                className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                onClick={beginEditing}
              >
                Edit
              </button>
            )}
            {showGenerateButton && (
              <button
                className="meeting-btn meeting-btn-ghost meeting-btn-sm"
                onClick={onGenerate}
                disabled={isGenerating || !hasTranscript}
                title={hasTranscript ? '' : 'A transcript is required first'}
              >
                {summary ? 'Regenerate' : 'Generate Minutes'}
              </button>
            )}
          </div>
        </div>

        {isGenerating ? (
          <div className="meeting-generating">
            <span className="spinner" /> Extracting decisions and action items from the transcript...
          </div>
        ) : !summary ? (
          <div className="meeting-empty-hint">
            {hasTranscript
              ? 'No minutes yet. Generate structured minutes with decisions and action items from the transcript.'
              : 'Minutes require a transcript. Paste the meeting transcript first.'}
          </div>
        ) : (
          <div>
            <div className="outcomes-stats">
              <div className="outcomes-stat">
                <span className="outcomes-stat-value">{decisions.length}</span>
                <span className="outcomes-stat-label">Decisions</span>
              </div>
              <div className="outcomes-stat outcomes-stat-carried">
                <span className="outcomes-stat-value">{carriedCount}</span>
                <span className="outcomes-stat-label">Carried</span>
              </div>
              <div className="outcomes-stat outcomes-stat-actions">
                <span className="outcomes-stat-value">{actionItems.length}</span>
                <span className="outcomes-stat-label">Actions</span>
              </div>
              <div className={`outcomes-stat ${unassignedActions > 0 ? 'outcomes-stat-warning' : ''}`}>
                <span className="outcomes-stat-value">{unassignedActions}</span>
                <span className="outcomes-stat-label">Unassigned</span>
              </div>
            </div>

            {summary.overview && (
              <div className="outcomes-overview" style={{ marginBottom: 0 }}>{summary.overview}</div>
            )}
          </div>
        )}
      </div>

      {!isGenerating && keyPoints.length > 0 && (
        <div className="meeting-panel">
          <div
            className="meeting-panel-header meeting-panel-header-collapsible"
            role="button"
            aria-expanded={showKeyPoints}
            onClick={() => setShowKeyPoints((s) => !s)}
          >
            <div>
              <h3 className="meeting-panel-title">Key Discussion Points</h3>
              <p className="meeting-panel-subtitle">{keyPoints.length} discussion highlights</p>
            </div>
            <span className={`meeting-panel-chevron ${showKeyPoints ? 'expanded' : ''}`}>▾</span>
          </div>
          {showKeyPoints && (
            <div className="agenda-list">
              {keyPoints.map((point, idx) => {
                const { lead, rest } = splitKeyPoint(point);
                return (
                  <div className="agenda-item" key={idx}>
                    <div className="agenda-item-index">{idx + 1}</div>
                    <div className="agenda-item-content">
                      <p className="keypoint-text">
                        {lead && <strong className="keypoint-lead">{lead}</strong>}
                        {lead ? ' — ' : ''}{rest}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!isGenerating && decisions.length > 0 && (
        <div className="meeting-panel">
          <div
            className="meeting-panel-header meeting-panel-header-collapsible"
            role="button"
            aria-expanded={showDecisions}
            onClick={() => setShowDecisions((s) => !s)}
          >
            <div>
              <h3 className="meeting-panel-title">Decisions</h3>
              <p className="meeting-panel-subtitle">
                {decisions.length} total · {carriedCount} carried
              </p>
            </div>
            <span className={`meeting-panel-chevron ${showDecisions ? 'expanded' : ''}`}>▾</span>
          </div>
          {showDecisions && (
          <div className="decision-cards">
            {decisions.map((d, idx) => {
              const oc = outcomeClass(d.outcome);
              return (
                <div className={`decision-card decision-card-${oc}`} key={idx}>
                  <div className="decision-card-body">
                    <span className="decision-card-title">{d.title}</span>
                    {d.moved_by && <span className="decision-card-meta">Moved by {d.moved_by}</span>}
                  </div>
                  <span className={`decision-outcome ${oc}`}>{d.outcome}</span>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {!isGenerating && actionItems.length > 0 && (
        <div className="meeting-panel">
          <div
            className="meeting-panel-header meeting-panel-header-collapsible"
            role="button"
            aria-expanded={showActions}
            onClick={() => setShowActions((s) => !s)}
          >
            <div>
              <h3 className="meeting-panel-title">Actions</h3>
              <p className="meeting-panel-subtitle">
                {actionItems.length} total{unassignedActions > 0 ? ` · ${unassignedActions} unassigned` : ''}
              </p>
            </div>
            <span className={`meeting-panel-chevron ${showActions ? 'expanded' : ''}`}>▾</span>
          </div>
          {showActions && (
          <div className="action-cards">
            {actionItems.map((a, idx) => (
              <div className="action-card" key={idx}>
                <div className={`action-card-avatar ${a.owner ? '' : 'unassigned'}`} title={a.owner || 'Unassigned'}>
                  {a.owner ? initials(a.owner) : '?'}
                </div>
                <div className="action-card-body">
                  <span className="action-card-desc">{a.description}</span>
                  <span className="action-card-meta">{a.owner || 'Unassigned'}</span>
                </div>
                {a.due_date && <span className="action-card-due">{a.due_date}</span>}
              </div>
            ))}
          </div>
          )}
        </div>
      )}
    </>
  );
};

export default MeetingMinutesPanel;
